<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <MainHeader :title="''"> </MainHeader>
    <div class="view">
      <!-- ONBOARDING COMPLETE ANIMATION -->
      <div class="onboarding-complete">
        <div class="burst-container" ref="burstRef"></div>

        <div class="circle-bg" ref="circleBgRef">
          <svg class="check-svg" viewBox="0 0 52 52" fill="none">
            <path
              class="check-path"
              ref="checkPathRef"
              d="M12 27 L22 38 L40 16"
              stroke="white"
              stroke-width="4.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <h1 class="oc-headline">You're all set!</h1>
        <p class="oc-sub">
          Everything's ready. Time to take control of your budget.
        </p>

        <button
          data-pt-name="Go to dashbord after creating account"
          data-pt-property-method="Dashboard"
          class="oc-cta"
          @click="goToDashboard"
        >
          Go to my Dashboard
        </button>

        <div class="oc-tags">
          <span class="tag tag-p">Profile complete</span>
          <span class="tag tag-t">Categories saved</span>
          <span class="tag tag-c">Account verified</span>
        </div>
      </div>
      <!-- END ONBOARDING COMPLETE ANIMATION -->

      <!-- <div>
        <p class="text-center">Now let's connect your bank accounts.</p>
      </div>
      <div class="user-tip">
        <div class="icon-container">
          <div class="icon-wrapper">
            <font-awesome-icon
              class="animated-gradient-icon"
              :icon="['fas', 'pen-to-square']"
            />
          </div>
        </div>
        <div>
          <p>You can add or remove bank connections at any time.</p>
        </div>
      </div> -->
      <!-- <button
        :disabled="!premiumUser"
        class="teal-button mt-3 w-100 mb-1"
        @click="accountConnectionSelection"
        data-pt-name="BankAccountLinked during create account"
        data-pt-property-provider="Plaid"
      >
        Connect Account
      </button>

      <div v-if="infoMessage" class="info-banner mt-3 mb-4">
        <p>{{ infoMessage }}</p>
        <button
          v-if="pendingUpdateInstitutionId"
          class="blue-button mt-2 w-100"
          @click="startUpdateFlow"
        >
          Refresh bank connection
        </button>
      </div> -->

      <div class="mt-6" v-if="accounts && accounts.length > 0">
        <div
          v-for="(institutionAccounts, institutionName) in groupedAccounts"
          :key="institutionName"
          class="bank-group"
        >
          <h3 class="bank-name">{{ institutionName }}</h3>
          <div
            v-for="account in institutionAccounts"
            :key="account"
            class="settings-list"
            @click="editAccount(account)"
          >
            <div>
              <font-awesome-icon
                :icon="
                  account.icon ? parseIconString(account.icon) : ['fas', 'user']
                "
                :style="{ color: account.iconColor }"
              />
            </div>
            <div>{{ account.displayName }}</div>
          </div>
        </div>
      </div>
    </div>
    <!-- <ProgressBar :currentStep="4" :steps="5" /> -->
    <ProgressBar
      :currentStep="3"
      :steps="4"
      :stepLabels="['Account Type', 'Your Details', 'Categories', 'All Done']"
    />
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { useMyStore } from '../../store.js';
import { onBeforeMount, onMounted, ref, watchEffect, computed } from 'vue';

import MainHeader from '../../components/View-Header.vue';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import ProgressBar from '../../components/ProgressBar.vue';

const DOTS = [
  { color: '#7F77DD', size: 14 },
  { color: '#5DCAA5', size: 10 },
  { color: '#D85A30', size: 12 },
  { color: '#D4537E', size: 8 },
  { color: '#EF9F27', size: 10 },
  { color: '#378ADD', size: 12 },
  { color: '#7F77DD', size: 8 },
  { color: '#5DCAA5', size: 14 },
  { color: '#D85A30', size: 10 },
  { color: '#D4537E', size: 12 },
  { color: '#EF9F27', size: 8 },
  { color: '#378ADD', size: 10 },
  { color: '#639922', size: 12 },
  { color: '#BA7517', size: 8 },
  { color: '#AFA9EC', size: 10 },
  { color: '#1D9E75', size: 14 },
];

const RINGS = ['#7F77DD', '#5DCAA5', '#D85A30'];

export default {
  components: {
    MainHeader,
    SkeletonLoader,
    ProgressBar,
  },
  setup() {
    const router = useRouter();
    const store = useMyStore();
    const linkToken = ref('');
    const accounts = ref([]);
    const loading = ref(true);
    const premiumUser = computed(() => store.user.premiumSubscription);

    const infoMessage = ref('');
    const pendingUpdateInstitutionId = ref(null);

    const burstRef = ref(null);
    const circleBgRef = ref(null);
    const checkPathRef = ref(null);

    const spawnParticles = () => {
      const burst = burstRef.value;
      if (!burst) return;
      burst.innerHTML = '';

      DOTS.forEach((d, i) => {
        const angle = (i / DOTS.length) * 360 + Math.random() * 22 - 11;
        const dist = 110 + Math.random() * 80;
        const rad = (angle * Math.PI) / 180;
        const tx = Math.cos(rad) * dist;
        const ty = Math.sin(rad) * dist;
        const sf = 0.5 + Math.random() * 0.8;

        const el = document.createElement('div');
        el.className = 'oc-dot';
        el.style.cssText = `
          width:${d.size}px; height:${d.size}px;
          background:${d.color};
          left:calc(50% - ${d.size / 2}px);
          top:calc(50% - 60px - ${d.size / 2}px);
          --tx:${tx}px; --ty:${ty}px; --sf:${sf};
          animation-delay:${0.2 + i * 0.025}s;
          animation-duration:${0.9 + Math.random() * 0.3}s;
        `;
        burst.appendChild(el);
      });

      RINGS.forEach((color, i) => {
        const w = 80 + i * 20;
        const el = document.createElement('div');
        el.className = 'oc-ring';
        el.style.cssText = `
          width:${w}px; height:${w}px;
          border-color:${color};
          left:calc(50% - ${w / 2}px);
          top:calc(50% - 60px - ${w / 2}px);
          animation-delay:${0.15 + i * 0.15}s;
        `;
        burst.appendChild(el);
      });
    };

    const groupedAccounts = computed(() => {
      const result = {};
      accounts.value.forEach((account) => {
        if (!result[account.institutionName]) {
          result[account.institutionName] = [];
        }
        result[account.institutionName].push(account);
      });
      const sortedResult = {};
      Object.keys(result)
        .sort()
        .forEach((institutionName) => {
          sortedResult[institutionName] = result[institutionName].sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
        });
      return sortedResult;
    });

    const openPlaidLink = (token) => {
      const isOAuthRedirect = window.location.href.includes('oauth_state_id');
      const handler = window.Plaid.create({
        token,
        ...(isOAuthRedirect && { receivedRedirectUri: window.location.href }),
        onSuccess,
        onExit,
      });
      handler.open();
    };

    const onSuccess = async (publicToken, metadata) => {
      try {
        loading.value = true;
        const resp = await store.exchangePublicToken(
          publicToken,
          metadata.institution,
          metadata.accounts
        );
        if (resp?.data?.requiresReauthentication) {
          infoMessage.value =
            `Looks like ${metadata.institution?.name || 'this bank'} is already connected. ` +
            `To keep your data in sync, please refresh the connection below.`;
          pendingUpdateInstitutionId.value =
            metadata.institution.institution_id;
          return;
        }
        await store.getPlaidAccountsList();
        await store.syncPlaidTransactions();
      } catch (error) {
        console.error('Plaid onSuccess flow error:', error);
        if (
          error?.message?.includes('duplicate') ||
          error?.response?.data?.requiresReauthentication
        ) {
          infoMessage.value =
            'This bank is already linked. Please refresh the connection below so we can keep your data up to date.';
          pendingUpdateInstitutionId.value =
            metadata.institution.institution_id;
          return;
        }
      } finally {
        loading.value = false;
        removePlaidLinkToken();
      }
    };

    const onExit = (error, metadata) => {
      console.log('hits onexit');
      console.log('error: ', error);
      console.log('metadata: ', metadata);
      if (metadata?.status === 'requires_oauth') {
        console.log('link token ' + linkToken.value);
      }
      removePlaidLinkToken();
    };

    const removePlaidLinkToken = () => {
      if (localStorage.getItem('plaid_link_token')) {
        localStorage.removeItem('plaid_link_token');
      }
    };

    const editAccount = (account) => {
      store.editAccount(account);
      router.push({
        name: 'EditAccount',
        params: { previousPage: 'ConnectedAccounts' },
      });
    };

    watchEffect(() => {
      accounts.value = store.accountsList;
    });

    onBeforeMount(() => {
      store.getPlaidAccountsList();
    });

    onMounted(() => {
      loading.value = false;

      const isOAuthRedirect = window.location.href.includes('oauth_state_id');
      const savedToken = localStorage.getItem('plaid_link_token');

      if (isOAuthRedirect && savedToken) {
        const handler = window.Plaid.create({
          token: savedToken,
          receivedRedirectUri: window.location.href,
          onSuccess,
          onExit,
        });
        handler.open();
      }

      spawnParticles();
    });

    const linkAccountAfterSelection = () => {
      try {
        store.createLinkToken().then(() => {
          linkToken.value = store.linkToken;
          localStorage.setItem('plaid_link_token', linkToken.value);
          openPlaidLink(linkToken.value);
        });
      } catch (error) {
        console.log('Error creating link token:', error);
      }
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const goToDashboard = () => {
      router.push({ name: 'Dashboard' });
    };

    const accountConnectionSelection = () => {
      linkAccountAfterSelection();
    };

    const startUpdateFlow = async () => {
      if (!pendingUpdateInstitutionId.value) return;
      try {
        loading.value = true;
        await store.startUpdateMode(pendingUpdateInstitutionId.value);
        await store.getPlaidAccountsList();
        infoMessage.value = '';
      } catch (err) {
        console.error('Error in update flow:', err);
        infoMessage.value =
          'We had trouble refreshing this connection. Please try again.';
      } finally {
        pendingUpdateInstitutionId.value = null;
        loading.value = false;
      }
    };

    if (!store) {
      return null;
    }

    return {
      router,
      openPlaidLink,
      accounts,
      groupedAccounts,
      editAccount,
      parseIconString,
      loading,
      goToDashboard,
      accountConnectionSelection,
      linkAccountAfterSelection,
      premiumUser,
      infoMessage,
      pendingUpdateInstitutionId,
      startUpdateFlow,
      burstRef,
      circleBgRef,
      checkPathRef,
    };
  },
};
</script>

<style scoped>
.onboarding-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem 2rem;
  position: relative;
  overflow: hidden;
}

.burst-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.oc-dot {
  position: absolute;
  border-radius: 50%;
  opacity: 0;
  animation: ocFlyOut 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes ocFlyOut {
  0% {
    transform: translate(0, 0) scale(0);
    opacity: 1;
  }
  60% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(var(--sf));
    opacity: 0;
  }
}

.oc-ring {
  position: absolute;
  border-radius: 50%;
  border: 3px solid;
  opacity: 0;
  animation: ocRingOut 1s ease-out forwards;
}

@keyframes ocRingOut {
  0% {
    transform: scale(0.3);
    opacity: 0.8;
  }
  100% {
    transform: scale(2.8);
    opacity: 0;
  }
}

.circle-bg {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7f77dd 0%, #5dcaa5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  animation: ocPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
  box-shadow: 0 12px 40px rgba(127, 119, 221, 0.35);
}

@keyframes ocPopIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.check-svg {
  width: 48px;
  height: 48px;
}

.check-path {
  stroke-dasharray: 80;
  stroke-dashoffset: 80;
  animation: ocDrawCheck 0.5s ease-out 0.55s forwards;
}

@keyframes ocDrawCheck {
  to {
    stroke-dashoffset: 0;
  }
}

.oc-headline {
  font-size: 1.75rem;
  font-weight: 800;
  margin: 1.25rem 0 0.35rem;
  text-align: center;
  color: #004751;
  animation: ocSlideUp 0.6s cubic-bezier(0.34, 1.2, 0.64, 1) 0.7s both;
  letter-spacing: -0.02em;
}

.dark-theme .oc-headline {
  color: #5dcaa5;
}

.oc-sub {
  font-size: 0.95rem;
  color: #666;
  text-align: center;
  animation: ocSlideUp 0.6s cubic-bezier(0.34, 1.2, 0.64, 1) 0.85s both;
  max-width: 260px;
  line-height: 1.5;
  margin: 0 0 1.5rem;
}

.dark-theme .oc-sub {
  color: #aac;
}

@keyframes ocSlideUp {
  0% {
    transform: translateY(16px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.oc-cta {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 0.75rem 2rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  animation: ocSlideUp 0.6s cubic-bezier(0.34, 1.2, 0.64, 1) 1s both;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  box-shadow: 0 6px 24px rgba(127, 119, 221, 0.3);
  width: 100%;
  max-width: 280px;
}

.oc-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 32px rgba(127, 119, 221, 0.4);
}

.oc-cta:active {
  transform: scale(0.97);
}

.oc-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  animation: ocSlideUp 0.5s ease 1.2s both;
  margin-top: 1.1rem;
}

.tag {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
}

.tag-p {
  background: #eeedfe;
  color: #3c3489;
}
.tag-t {
  background: #e1f5ee;
  color: #0f6e56;
}
.tag-c {
  background: #faece7;
  color: #712b13;
}

.settings-list {
  display: grid;
  grid-template-columns: 10% 90%;
  box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
  padding: 1em;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 1em;
}

.light-theme .settings-list {
  background: white;
}

.dark-theme .settings-list {
  background: #212e3e;
  color: white;
}

.bank-name {
  color: #004751;
  text-align: center;
  padding-bottom: 0.5em;
  font-size: 1.1em !important;
  font-weight: bold;
}

.bank-group {
  padding: 1em;
  border-radius: 5px;
  margin-bottom: 2.5em;
}

.light-theme .bank-group {
  background: #f8f8f8;
  box-shadow: 0px 20px 20px rgba(0, 0, 0, 0.1);
}

.dark-theme .bank-group {
  background: #212e3e;
  color: white;
}

.user-tip {
  border: 1px solid #e2e2e2;
  text-align: center;
  align-items: center;
  box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 1em;
  row-gap: 0.5em;
  margin-bottom: 1em;
}

.light-theme .user-tip {
  background: white;
}

.dark-theme .user-tip {
  background: #212e3e;
}

.user-tip .icon-container {
  text-align: center;
  justify-content: center;
  display: flex;
}

.info-banner {
  padding: 0.75em 1em;
  border-radius: 5px;
  font-size: 0.9rem;
  text-align: left;
}

.light-theme .info-banner {
  background: #e6f4ff;
  color: #004751;
  border: 1px solid #b3dcff;
}

.dark-theme .info-banner {
  background: #102a43;
  color: #e0f2fe;
  border: 1px solid #1d4ed8;
}
</style>
